/**
 * useSpeechSynthesis — TTS via /api/app/tts (Voxtral PCM streaming)
 * Mirrors the geno-manus approach: PCM chunks → PcmPlayer (Web Audio API)
 */
import { useState, useCallback, useRef, useEffect } from "react";

interface UseSpeechSynthesisOptions {
  voicePreference?: "annam" | "christopher";
}

const PLAYBACK_SAMPLE_RATE = 24000;
const BYTES_PER_SAMPLE = 4;

class PcmPlayer {
  private ctx: AudioContext;
  private gainNode: GainNode;
  private node: ScriptProcessorNode;
  private inputRate: number;
  private chunks: Float32Array[] = [];
  private chunkOffset = 0;
  private bufferedSamples = 0;
  private started = false;
  private startupSamples = 0;
  private carry: Uint8Array = new Uint8Array(0);

  constructor(inputSampleRate: number) {
    this.inputRate = inputSampleRate;
    this.ctx = new AudioContext();
    this.gainNode = this.ctx.createGain();
    this.gainNode.gain.value = 1;
    this.node = this.ctx.createScriptProcessor(2048, 1, 1);
    this.startupSamples = Math.floor(this.ctx.sampleRate * 0.05);

    this.node.onaudioprocess = event => {
      const out = event.outputBuffer.getChannelData(0);
      let writePos = 0;

      if (!this.started) {
        if (this.bufferedSamples < this.startupSamples) {
          out.fill(0);
          return;
        }
        this.started = true;
      }

      while (writePos < out.length && this.chunks.length > 0) {
        const head = this.chunks[0];
        const remaining = head.length - this.chunkOffset;
        const needed = out.length - writePos;
        const toCopy = Math.min(remaining, needed);
        out.set(head.subarray(this.chunkOffset, this.chunkOffset + toCopy), writePos);
        writePos += toCopy;
        this.chunkOffset += toCopy;
        this.bufferedSamples -= toCopy;
        if (this.chunkOffset >= head.length) {
          this.chunks.shift();
          this.chunkOffset = 0;
        }
      }

      if (writePos < out.length) out.fill(0, writePos);

      if (this.bufferedSamples <= 0) {
        this.bufferedSamples = 0;
        this.started = false;
      }
    };

    this.node.connect(this.gainNode);
    this.gainNode.connect(this.ctx.destination);
  }

  private resampleLinear(input: Float32Array, outputRate: number): Float32Array {
    if (this.inputRate === outputRate) return input;
    const ratio = this.inputRate / outputRate;
    const outLength = Math.max(1, Math.floor(input.length / ratio));
    const out = new Float32Array(outLength);
    for (let i = 0; i < outLength; i++) {
      const sourceIndex = i * ratio;
      const left = Math.floor(sourceIndex);
      const right = Math.min(left + 1, input.length - 1);
      const frac = sourceIndex - left;
      out[i] = input[left] * (1 - frac) + input[right] * frac;
    }
    return out;
  }

  feed(pcmBuffer: ArrayBuffer): void {
    if (this.ctx.state === "suspended") void this.ctx.resume();

    let incoming = new Uint8Array(pcmBuffer);
    let bytes: Uint8Array;

    if (this.carry.length > 0) {
      bytes = new Uint8Array(this.carry.length + incoming.length);
      bytes.set(this.carry);
      bytes.set(incoming, this.carry.length);
    } else {
      bytes = incoming.slice();
    }

    const usable = bytes.length - (bytes.length % BYTES_PER_SAMPLE);
    this.carry = bytes.slice(usable);
    if (usable === 0) return;

    const float32 = new Float32Array(bytes.buffer, bytes.byteOffset, usable / BYTES_PER_SAMPLE);
    const converted = this.resampleLinear(float32, this.ctx.sampleRate);
    this.chunks.push(converted);
    this.bufferedSamples += converted.length;
  }

  get isPlaying(): boolean {
    return this.bufferedSamples > 0;
  }

  clear(): void {
    this.chunks = [];
    this.chunkOffset = 0;
    this.bufferedSamples = 0;
    this.started = false;
    this.carry = new Uint8Array(0);
  }

  close(): void {
    this.clear();
    try { this.node.disconnect(); } catch {}
    try { this.gainNode.disconnect(); } catch {}
    void this.ctx.close();
  }
}

export function useSpeechSynthesis(options?: UseSpeechSynthesisOptions) {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentMsgId, setCurrentMsgId] = useState<string | null>(null);

  const playerRef = useRef<PcmPlayer | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const speakingCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stop = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (speakingCheckRef.current) {
      clearInterval(speakingCheckRef.current);
      speakingCheckRef.current = null;
    }
    if (playerRef.current) {
      playerRef.current.close();
      playerRef.current = null;
    }
    setIsSpeaking(false);
    setIsLoading(false);
    setCurrentMsgId(null);
  }, []);

  useEffect(() => {
    return () => { stop(); };
  }, [stop]);

  const speak = useCallback(
    async (text: string, msgId: string) => {
      if ((isSpeaking || isLoading) && currentMsgId === msgId) {
        stop();
        return;
      }

      stop();

      setIsLoading(true);
      setCurrentMsgId(msgId);

      const ac = new AbortController();
      abortRef.current = ac;

      try {
        const voiceId = options?.voicePreference || "annam";

        const response = await fetch("/api/app/tts", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ text, voiceId }),
          signal: ac.signal,
        });

        if (!response.ok || !response.body) {
          console.error("[TTS] Stream failed:", response.status);
          setIsLoading(false);
          setIsSpeaking(false);
          setCurrentMsgId(null);
          return;
        }

        if (ac.signal.aborted) return;

        playerRef.current = new PcmPlayer(PLAYBACK_SAMPLE_RATE);
        const player = playerRef.current;

        setIsLoading(false);
        setIsSpeaking(true);

        const reader = response.body.getReader();
        try {
          while (true) {
            if (ac.signal.aborted) break;
            const { done, value } = await reader.read();
            if (done) break;
            if (value) player.feed(value.buffer);
          }
        } finally {
          reader.releaseLock();
        }

        // Wait for PCM buffer to drain
        await new Promise<void>(resolve => {
          const check = setInterval(() => {
            if (!player.isPlaying || ac.signal.aborted) {
              clearInterval(check);
              speakingCheckRef.current = null;
              resolve();
            }
          }, 100);
          speakingCheckRef.current = check;
        });

      } catch (err: unknown) {
        if (err instanceof Error && err.name === "AbortError") return;
        console.error("[TTS] request failed:", err);
      } finally {
        if (!ac.signal.aborted) {
          setIsSpeaking(false);
          setIsLoading(false);
          setCurrentMsgId(null);
        }
      }
    },
    [isSpeaking, isLoading, currentMsgId, stop, options?.voicePreference]
  );

  return {
    speak,
    stop,
    isSpeaking,
    isLoading,
    currentMsgId,
    isSupported: true,
  };
}
